// Single source of truth for the *physical* data-cache key layout.
//
// nuxt-multi-cache stores every data-cache item under `${prefix}--${itemKey}`
// (see node_modules/nuxt-multi-cache .../helpers/server.js -> getCacheKeyWithPrefix),
// where `prefix` is whatever `cacheKeyPrefix(event)` returns in
// server/multiCache.serverOptions.ts. We reproduce that layout here so the
// Kafka-driven purge path (utils/Cache.ts) can evict a specific entry BY KEY
// — bypassing the tag-invalidation debounce — and so the prefix is defined in
// exactly one place. Both the writer (serverOptions) and the purger build their
// keys from these helpers; if the format drifts, eviction silently misses, so
// the literal layout is pinned in cacheKeys.test.ts.

/** The per-request data-cache prefix: `${tenantId}:${jobShopId}:`. */
export function dataCacheKeyPrefix(tenantId: string, jobShopId: string): string {
  return `${tenantId}:${jobShopId}:`
}

/** The full physical key a data-cache item is stored under, including the `--` separator. */
export function buildDataCacheKey(tenantId: string, jobShopId: string, itemKey: string): string {
  return `${dataCacheKeyPrefix(tenantId, jobShopId)}--${itemKey}`
}

/**
 * The data-cache item key used by useJobPageTemplateData for a given templateId.
 * Default-template jobs carry `template_id = ""` (item key `jobPageTemplate-`),
 * but the caller passes `job.template_id ?? null`, so a missing id yields
 * `jobPageTemplate-null`. Both variants are purged on DEFAULT_TEMPLATE_CHANGED.
 * The `${templateId}` interpolation mirrors the original key exactly, so this is
 * the single source of truth for what the composable writes and the purger evicts.
 */
export function jobPageTemplateCacheItemKey(templateId: string | null): string {
  return `jobPageTemplate-${templateId}`
}

/**
 * The data-cache item key used by useContentModules for a given pageId.
 * The published-page Kafka message carries only the pageId, so this is the one
 * entry whose stale preset content can be evicted by key on PUBLISHED_PAGE.
 * The composable builds its key from here too, so writer and purger cannot drift.
 */
export function contentModulesCacheItemKey(pageId: string): string {
  return `contentModules-${pageId}`
}

/**
 * The data-cache item key used by useJobShopSettings, holding the whole
 * `jobshop.*` namespace for one JobShop.
 *
 * The physical key already carries `${tenantId}:${jobShopId}:` (see
 * dataCacheKeyPrefix), so a constant item key is correctly scoped — there is
 * exactly one set of JobShop settings per JobShop, hence a plain constant rather
 * than a builder like the two above.
 *
 * Like the two above it has a purger counterpart — purgeJobShopSettingsCache —
 * so both sides build the key from here and cannot drift. The tenant it needs
 * comes from the `Tenant` header (note: `Tenant`, not `TenantId`) that
 * setting-service puts on every message it publishes.
 */
export const JOB_SHOP_SETTINGS_CACHE_ITEM_KEY = "jobShopSettings"

/**
 * The Redis key prefix for applicant-source sessions (JET-2720).
 *
 * Deliberately not keyed by build version, unlike the cache bases: a wiped cache
 * is refetched, a wiped session loses the attribution the feature exists to
 * keep. Mounted in server/plugins/storage.ts and mirrored by `devStorage` in
 * nuxt.config.ts, which is why it lives here rather than in the server util that
 * reads it — nuxt.config cannot import server code.
 */
export const TRACKING_STORAGE_BASE = "candidate-frontend:tracking:"
